/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/* eslint-disable no-case-declarations */
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import keys from 'lodash/keys';
import merge from 'lodash/merge';
import set from 'lodash/set';
import size from 'lodash/size';
import {
    calendarDateToUtcString,
    localToday,
    utcStringToCalendarDate,
} from 'sarsaparilla';
import { endOfMonth, CalendarDate } from '@internationalized/date';
import { GRID_SORT } from '../constants/campground';
import {
    getTodayISO,
    isDateBefore,
    isValidSimpleDate,
    formatMDYShort,
} from '../utils/dateUtils';
import {
    ADD_SEARCH_FILTER,
    CLEAR_CAMPSITE_AVAILABILITY,
    CLEAR_LTS_CAMPSITE,
    CLEAR_RATE_LIMITED_MONTHS,
    CLEAR_SELECTIONS,
    CLOSE_ACCESSIBILITY_MODAL,
    CLOSE_CAMPGROUND_FILTERS_MODAL,
    CLOSE_ITINERARY_DETAILS_MODAL,
    CLOSE_RENTAL_MODAL,
    COMPILE_FINAL_DISPLAYABLE_AVAILABILITIES,
    CONTINUE_LOADING,
    FETCH_AVAILABILITY_DATA_FAILURE,
    FETCH_AVAILABILITY_DATA_SUCCESS,
    FETCH_CAMPSITES_AVAILABILITY,
    FETCH_CAMPSITES_AVAILABILITY_LOADING,
    FETCH_MONTHLY_AVAILABILITY,
    FILTER_AVAILABILITIES_BY_INPUT_COMPLETE,
    FILTER_AVAILABILITIES_BY_SEARCH_COMPLETE,
    GET_AVAILABLE_CAMPSITES,
    INIT_CAMPGROUND_AVAILABILITY_STATES,
    MAP_CAMPSITE_IDS_DESELECTED,
    MAP_CAMPSITE_IDS_SELECTED,
    RESET_MAP_VIEW,
    NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX,
    OPEN_ACCESSIBILITY_MODAL,
    OPEN_CAMPGROUND_FILTERS_MODAL,
    OPEN_ITINERARY_DETAILS_MODAL,
    OPEN_RENTAL_MODAL,
    PAUSE_LOADING,
    PROCESS_AVAILABILITY_COMPLETE,
    REMOVE_SEARCH_FILTER,
    RESERVATION_DETAILS_TOGGLE,
    SELECT_LTS_CAMPSITE,
    SET_AVAILABILITY_QUERY_ENABLED,
    SET_FILTER_INPUT,
    SET_GRID_RANGE,
    SET_GRID_SIZE,
    SET_RATE_LIMITED_MONTHS,
    SET_SORT_PROPERTY,
    SORT_AVAILABILITIES_COMPLETE,
    TOGGLE_FILTER_AS_YOU_MOVE,
    UPDATE_AVAILABILITIES_COMPLETE,
    UPDATE_ESTIMATED_PRICE,
    UPDATE_SELECTIONS,
    UPDATE_SELECTION_BY_ID,
    UPDATE_SELECTION_WITH_RES_ID,
    UPDATE_SINGLE_CAMPSITE_AVAILABILITIES_COMPLETE,
} from '../constants';

const getSortObjValues = () => {
    let sortObj;
    const defaultSort = {
        sortedColumnId: 'AVAILABILITY',
        isSortAsc: true,
    };

    try {
        const prevSort = localStorage.getItem(GRID_SORT);
        if (prevSort) {
            sortObj = JSON.parse(prevSort);

            if (isValidSimpleDate(sortObj.sortedColumnId)) {
                const [month, day, year] = sortObj.sortedColumnId.split('-');
                const sortDate = new CalendarDate(year, month, day);
                if (isDateBefore(sortDate, localToday)) {
                    sortObj.sortedColumnId = formatMDYShort(localToday, '-');
                }
            }
        } else {
            sortObj = defaultSort;
        }
    } catch (err) {
        sortObj = defaultSort;
    }
    return sortObj;
};

const INITIAL_STATE = {
    selections: {},
    estimatedPrices: {
        minPriceEstimate: 0,
        maxPriceEstimate: 0,
        isLoading: false,
    },
    availabilitiesByMonth: {},
    campsitesFullAvailability: {},
    originalAvailabilitiesMap: {},
    processedAvailabilitiesMap: {},
    filteredAvailabilitiesBySearch: {},
    filteredAvailabilitiesByInput: {},
    filteredAvailabilitiesByMapIds: {},
    resetMapViewCounter: 0,
    sortedAvailabilities: [],
    filteredSortedAvailabilities: [],
    isLoading: true,
    groupSize: '',
    filterText: '',
    availabilitiesEndDate: getTodayISO(),
    gridStartDate: getTodayISO(),
    gridEndDate: calendarDateToUtcString(
        localToday.add({ days: NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX })
    ),
    sort: getSortObjValues(),
    hasAvailabilityWithinView: true,
    counts: {},
    earlyAccessCounts: {},
    earlyAccessAvailableCounts: {},
    slidingCounts: {},
    ffCounts: {},
    countsLastFetched: {},
    isTransfer: false,
    error: null,
    salesChannel: '',
    isLoadingCampsitesAvailabilities: false,
    isRentalModalOpen: false,
    isAccessibilityModalOpen: false,
    isItineraryDetailsModalOpen: false,
    isCampgroundFiltersModalOpen: false,
    gridSize: NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX,
    gridSizeChanged: false,
    LTSCampsiteSelection: {
        startDate: null,
        campsiteId: null,
    },
    scrollToCampsite: null,
    reservationDetailsToggleValue: false,
    shouldFilterMapCampsites: localStorage.getItem('mapFilter') === 'true',
    /* When true, useMonthlyAvailability drives fetching and fetchMonthlyAvailabilityEpic
    no-ops. Set by pages that mount the hook, cleared when they unmount. */
    isAvailabilityQueryEnabled: false,
    availabilityRefreshCount: 0,
};

export default function availabilityGrid(state = INITIAL_STATE, action) {
    const st = { ...state };

    switch (action.type) {
        case FETCH_MONTHLY_AVAILABILITY:
            st.isLoading = true;
            /* shouldRefresh means "bypass the cache". Surfaced as a counter so
            useMonthlyAvailability can invalidate without every caller needing a hook. */
            if (action.payload?.shouldRefresh) {
                st.availabilityRefreshCount += 1;
            }
            return st;

        case FETCH_AVAILABILITY_DATA_SUCCESS:
            st.originalAvailabilitiesMap = action.combinedAvailabilities;
            st.availabilitiesByMonth = action.availabilitiesByMonth;
            const endDateIso = keys(action.availabilitiesByMonth).sort()[
                size(action.availabilitiesByMonth) - 1
            ];

            st.availabilitiesEndDate = calendarDateToUtcString(
                endOfMonth(utcStringToCalendarDate(endDateIso))
            );
            return st;

        case PROCESS_AVAILABILITY_COMPLETE:
            st.originalAvailabilitiesMap = action.payload;
            return st;

        case FETCH_AVAILABILITY_DATA_FAILURE:
            st.isLoading = false;
            return st;

        case ADD_SEARCH_FILTER:
        case REMOVE_SEARCH_FILTER:
            st.isLoading = true;
            return st;

        case FILTER_AVAILABILITIES_BY_SEARCH_COMPLETE:
            st.filteredAvailabilitiesBySearch = action.payload;
            return st;

        case SET_FILTER_INPUT:
            st.isLoading = true;
            st.filterText = action.payload;
            return st;

        case GET_AVAILABLE_CAMPSITES:
            st.isLoading = true;
            return st;

        case FILTER_AVAILABILITIES_BY_INPUT_COMPLETE:
            st.filteredAvailabilitiesByInput = action.payload;
            return st;

        case SET_SORT_PROPERTY:
            const { isAsc, columnId } = action.payload;
            let newIsSortAsc = isAsc;
            if (isNil(newIsSortAsc)) {
                newIsSortAsc =
                    st.sort.sortedColumnId === columnId ? !st.sort.isSortAsc : true;
            }

            const sortObj = {
                sortedColumnId: columnId,
                isSortAsc: newIsSortAsc,
            };
            localStorage.setItem(GRID_SORT, JSON.stringify(sortObj));

            st.sort = sortObj;
            return st;

        case SORT_AVAILABILITIES_COMPLETE:
            st.sortedAvailabilities = action.payload;
            st.scrollToCampsite = action.scrollToCampsite;
            return st;

        case COMPILE_FINAL_DISPLAYABLE_AVAILABILITIES:
            st.filteredSortedAvailabilities = action.payload;
            st.isLoading = false;
            return st;

        case UPDATE_AVAILABILITIES_COMPLETE:
            st.processedAvailabilitiesMap = action.payload.newAvailabilities;
            if (action.payload.newSelections) {
                st.selections = action.payload.newSelections;
            }
            st.isLoading = action.payload.shouldContinueLoading;
            return st;

        case UPDATE_SINGLE_CAMPSITE_AVAILABILITIES_COMPLETE:
            const newAvailabilitiesAtCampsite = {
                ...st.processedAvailabilitiesMap[action.payload.campsiteId],
                availabilities: action.payload.newCampsiteAvailabilities,
            };
            set(
                st.processedAvailabilitiesMap,
                action.payload.campsiteId,
                newAvailabilitiesAtCampsite
            );
            st.selections = action.payload.newSelections;
            st.isLoading = false;
            return st;

        case UPDATE_SELECTIONS: // also handled by EPIC: updateAvailabilityEpic
            const { campsiteId } = action.payload;

            if (size(st.selections) >= 10 && !has(st.selections, campsiteId)) {
                st.error =
                    'You have already reached the max allowed number of campsite selections. Please adjust your selection.';
                return st;
            }

            set(st, 'estimatedPrices.isLoading', true);

            return st;

        case UPDATE_SELECTION_BY_ID:
            set(st.selections, action.payload.campsiteId, action.payload.selection);
            return st;

        case UPDATE_SELECTION_WITH_RES_ID:
            set(
                st.selections,
                `${action.payload.campsiteId}.reservation_id`,
                action.payload.reservationId
            );
            return st;

        case UPDATE_ESTIMATED_PRICE:
            st.estimatedPrices = action.payload;
            return st;

        case CLEAR_SELECTIONS:
            st.selections = {};
            st.estimatedPrices = {};
            st.error = null;
            return st;

        case SET_GRID_RANGE:
            st.gridStartDate = action.payload.startDate;
            st.gridEndDate = action.payload.endDate;
            return st;

        case PAUSE_LOADING:
            st.isLoading = false;
            return st;

        case CONTINUE_LOADING:
            st.isLoading = true;
            return st;

        case OPEN_RENTAL_MODAL:
            st.isRentalModalOpen = true;
            return st;

        case CLOSE_RENTAL_MODAL:
            st.isRentalModalOpen = false;
            return st;

        case OPEN_ACCESSIBILITY_MODAL:
            st.isAccessibilityModalOpen = true;
            return st;

        case CLOSE_ACCESSIBILITY_MODAL:
            st.isAccessibilityModalOpen = false;
            return st;

        case OPEN_ITINERARY_DETAILS_MODAL:
            st.isItineraryDetailsModalOpen = true;
            return st;

        case CLOSE_ITINERARY_DETAILS_MODAL:
            st.isItineraryDetailsModalOpen = false;
            return st;

        case OPEN_CAMPGROUND_FILTERS_MODAL:
            st.isCampgroundFiltersModalOpen = true;
            return st;

        case CLOSE_CAMPGROUND_FILTERS_MODAL:
            st.isCampgroundFiltersModalOpen = false;
            return st;
        case SET_GRID_SIZE:
            st.gridSizeChanged = true;
            st.gridSize = action.gridSize;
            st.gridEndDate = calendarDateToUtcString(
                utcStringToCalendarDate(st.gridStartDate).add({
                    days: action.gridSize,
                })
            );
            return st;

        case INIT_CAMPGROUND_AVAILABILITY_STATES:
            st.salesChannel = action.payload.salesChannel;
            return st;

        case FETCH_CAMPSITES_AVAILABILITY:
            st.isLoadingCampsitesAvailabilities = false;
            st.campsitesFullAvailability = {};

            forEach(action.payload, (availability, key) => {
                st.campsitesFullAvailability[key] = availability;
            });

            return st;

        case FETCH_CAMPSITES_AVAILABILITY_LOADING:
            st.isLoadingCampsitesAvailabilities = true;
            return st;

        case RESERVATION_DETAILS_TOGGLE:
            st.reservationDetailsToggleValue = !st.reservationDetailsToggleValue;
            return st;

        case SELECT_LTS_CAMPSITE:
            st.LTSCampsiteSelection = {
                campsiteId: action.payload.campsiteId,
                startDate: action.payload.startDate,
            };
            return st;
        case CLEAR_LTS_CAMPSITE:
            st.LTSCampsiteSelection = {
                campsiteId: null,
                startDate: null,
            };
            return st;
        case SET_AVAILABILITY_QUERY_ENABLED:
            st.isAvailabilityQueryEnabled = action.payload;
            return st;
        case SET_RATE_LIMITED_MONTHS:
            st.rateLimitedMonths = merge({}, st.rateLimitedMonths, action.months);
            return st;
        case CLEAR_RATE_LIMITED_MONTHS:
            st.rateLimitedMonths = {};
            return st;
        case CLEAR_CAMPSITE_AVAILABILITY:
            st.availabilitiesByMonth = {};
            st.campsitesFullAvailability = {};
            st.originalAvailabilitiesMap = {};
            st.processedAvailabilitiesMap = {};
            return st;
        case MAP_CAMPSITE_IDS_SELECTED:
            const campsiteIds = action.payload || [];

            st.filteredAvailabilitiesByMapIds = campsiteIds.reduce((acc, id) => {
                acc[id] = true;
                return acc;
            }, {});
            return st;
        case MAP_CAMPSITE_IDS_DESELECTED:
            st.filteredAvailabilitiesByMapIds = {};
            return st;
        case RESET_MAP_VIEW:
            st.resetMapViewCounter += 1;
            return st;
        case TOGGLE_FILTER_AS_YOU_MOVE:
            st.shouldFilterMapCampsites =
                action.payload !== undefined
                    ? action.payload
                    : !st.shouldFilterMapCampsites;
            localStorage.setItem('mapFilter', st.shouldFilterMapCampsites.toString());
            return st;
        default:
            return st;
    }
}
